/* eslint-disable fp/no-mutating-methods */
import { useCallback, useEffect, useMemo, useRef, useState, FC, ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { v4 as uuid } from 'uuid'

import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import DotFilled from '~/assets/bootcamp/activities/long-multi/dot_filled'
import DotOutlined from '~/assets/bootcamp/activities/long-multi/dot_outlined'
import CommaDashed from '~/assets/bootcamp/activities/long-multi/comma-dashed'
import CommaFilled from '~/assets/bootcamp/activities/long-multi/comma-filled'
import SVGImage from '~/components/Image/SVGImage'
import { fromEntries } from '~/utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFocus from '../../hooks/useFocus'

import { LatticeCarriedNumber, LatticeSumInputWithDecimal } from './components/LatticeSumInput'
import { LatticeCell } from './components/LatticeCell'
import { LatticeDigitWithDecimal } from './components/LatticeDigit'
import {
  generateLatticeDecimal,
  generateLatticeGridTemplate,
  getCellDefaultValues,
  getSumCellDefaultValues,
  multiplyDecimals,
} from './helpers'
import { Cell, CellName, LatticeRow, LatticeRowType } from './types'

const LatticeDecimal: FC<ActivityProps> = ({
  difficultyLevel,
  actionButtonsFC,
  answerState,
  onAnswerStateChange,
  onNextQuestionClick,
  onInstanceChange,
  autofocus = true,
}) => {
  const [latticeArray, setLatticeArray] = useState<LatticeRow[]>([])
  const [gridColumns, setGridColumns] = useState<number>(0)
  const [gridRows, setGridRows] = useState<number>(0)
  const [multiplier, setMultiplier] = useState<string>('')
  const [multiplicand, setMultiplicand] = useState<string>('')
  const [multiplicandCommaIndex, setMultiplicandCommaIndex] = useState<number>(0)
  const [multiplierCommaIndex, setMultiplierCommaIndex] = useState<number>(0)
  const [decimalIndex, setDecimalIndex] = useState<string>()
  const [errors, setErrors] = useState<string[]>([])
  const [answerCells, setAnswerCells] = useState<Record<string, Cell>>()
  const [cellsSumAnswers, setCellsSumAnswers] = useState<Record<string, string>>({})
  const [carriedNumbers, setCarriedNumber] = useState<Record<string, string>>({})
  const [inputKey, setInputKey] = useState<string>(uuid())
  const { t } = useTranslation('imageAlt')

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { locale: language } = useRouter()
  const DecimalDashed = language === 'en' ? DotOutlined : CommaDashed
  const DecimalFilled = language === 'en' ? DotFilled : CommaFilled

  const generateActivity = useCallback(() => {
    const latticeData = generateLatticeDecimal(difficultyLevel)
    const _multiplierCommaIndex = String(latticeData.multiplier).indexOf('.')
    const _multiplicandCommaIndex =
      String(latticeData.multiplicand).indexOf('.') > 0 ? String(latticeData.multiplicand).indexOf('.') : 0

    setLatticeArray(latticeData.latticeArray)
    setGridColumns(latticeData.gridColumns)
    setGridRows(latticeData.gridRows)
    setMultiplier(latticeData.multiplier)
    setMultiplicand(latticeData.multiplicand)
    setMultiplicandCommaIndex(_multiplicandCommaIndex)
    setMultiplierCommaIndex(_multiplierCommaIndex)
    setAnswerCells(getCellDefaultValues(latticeData.gridColumns + latticeData.gridRows))
    setCellsSumAnswers(getSumCellDefaultValues(latticeData.latticeArray))
  }, [difficultyLevel])

  useEffect(() => {
    generateActivity()
  }, [generateActivity])

  // Only the two factors — the worked lattice is the answer; the separator stays ".", Norwegian
  // re-paints "," downstream.
  useEmitInstance(
    onInstanceChange,
    multiplier && multiplicand ? `${multiplier} \\cdot ${multiplicand} = \\square` : null,
  )

  const { givenAnswer, isFormValid } = useMemo(() => {
    const answerObj = Object.values(cellsSumAnswers)
    const values = answerObj.filter((item) => Boolean(item))
    const _givenAnswer = answerObj.join('')

    const _isFormValid = values.length > 0

    return { givenAnswer: _givenAnswer, isFormValid: _isFormValid }
  }, [cellsSumAnswers])

  const handleCellValueChange = (value: string, name: CellName): void => {
    const [sumIndex] = name.split(':') as [string, CellName]
    setAnswerCells((prev) => {
      const modified = { ...prev }
      modified[sumIndex][name] = value
      return modified
    })
  }

  const handleSumValueChange = (value: string, name: string): void => {
    setCellsSumAnswers((prev) => ({ ...prev, [name]: value }))
  }

  const handleCarriedNumberChange = (value: string, name: string): void => {
    setCarriedNumber((prev) => ({ ...prev, [name]: value }))
  }

  const handleOnNextQuestionClick = (): void => {
    clear()
    onNextQuestionClick()
    onAnswerStateChange(null)
    generateActivity()
    setInputKey(uuid())
  }

  function handleOnCheckAnswerClick(): void {
    const answer = String(multiplyDecimals(Number(multiplicand), Number(multiplier)))
    const givenAnsArr = Array.from(givenAnswer)
    const answerArr = Array.from(answer)
    const answerEntries = Object.entries(cellsSumAnswers)
    const isAnswerDecimal = !Number.isInteger(Number(answer))

    let ansWithoutDecimalAr = new Array(gridColumns + gridRows + (isAnswerDecimal ? 1 : 0)).fill(undefined)
    ansWithoutDecimalAr = ansWithoutDecimalAr
      .slice(0, ansWithoutDecimalAr.length - answerArr.length)
      .concat(...Array.from(answerArr))

    const ansDecimalIndex = ansWithoutDecimalAr.indexOf('.')
    const ansLengthDiff = answerEntries.length - (isAnswerDecimal ? answer.length - 1 : answer.length)

    const constructedAnswer = new Array(ansLengthDiff).fill(0).concat(...givenAnsArr)

    if (constructedAnswer.length > answerEntries.length) {
      constructedAnswer.splice(0, constructedAnswer.length - answerEntries.length)
    }

    if (ansDecimalIndex > 0) {
      ansWithoutDecimalAr.splice(ansDecimalIndex, 1)
    }

    if (decimalIndex) {
      constructedAnswer.splice(Number(decimalIndex), 0, '.')
    }

    let sumErrors: string[] = []

    const isCorrectAns = Number(answer) === Number(constructedAnswer.join(''))

    if (!isCorrectAns) {
      sumErrors = answerEntries
        .filter(([key, value]) => {
          const [, sumIndex] = key.split(':')
          const ansDigit = Number(ansWithoutDecimalAr[Number(sumIndex) - 1])

          if ((value === '' || value === '0') && Number.isNaN(ansDigit)) {
            return false
          }

          return !value || ansDigit !== Number(value)
        })
        .map(([key]) => key)
    }

    onAnswerStateChange(isCorrectAns)

    if (isCorrectAns) {
      setErrors([])
      return
    }

    if (decimalIndex && Number(decimalIndex) !== ansDecimalIndex) {
      sumErrors.push(`decimalError:${decimalIndex}`)
    } else if (!decimalIndex && ansDecimalIndex > 0) {
      sumErrors.push(`decimalError`)
    }

    setErrors(sumErrors)
  }

  const onEnterPress = (): void => {
    hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
  }

  const clear = (): void => {
    setAnswerCells(getCellDefaultValues(gridRows + gridColumns))
    setCellsSumAnswers((prev) => {
      const cleared = Object.entries(prev).map(([key]) => [key, ''])
      return fromEntries(cleared)
    })
    setErrors([])
    setCarriedNumber({})
    setDecimalIndex('')
  }

  const onCommaClick = (val: string): void => {
    setDecimalIndex((prev) => {
      return prev === val ? '' : val
    })
  }

  const missingDecimal = errors.includes('decimalError')

  function renderItem(item: LatticeRow, index: number): ReactNode {
    switch (item.type) {
      case LatticeRowType.CELL: {
        const [field1Name, field2Name] = item.names as CellName[]
        const value1 = answerCells?.[field1Name.split(':')[0]]?.[field1Name] || ''
        const value2 = answerCells?.[field2Name.split(':')[0]]?.[field2Name] || ''

        return (
          <LatticeCell
            key={`${item.name}-${index}-${inputKey}-cell`}
            names={item.names}
            onChange={handleCellValueChange}
            onEnter={onEnterPress}
            values={[value1, value2]}
          />
        )
      }

      case LatticeRowType.NUM:
        return (
          <LatticeDigitWithDecimal
            carriedName={item.carriedName}
            carriedValue={carriedNumbers[item.carriedName] || ''}
            decimalIndexes={[multiplierCommaIndex, multiplicandCommaIndex]}
            decimalPosition={item.decimalPosition}
            digit={item.value}
            hasCarriedNumber={item.hasCarriedNumber}
            itemIndex={item.itemIndex}
            key={`${item.decimalPosition}-${item.itemIndex}-${inputKey}-digit`}
            onCarriedChange={handleCarriedNumberChange}
            onEnter={onEnterPress}
          />
        )

      case LatticeRowType.SUM: {
        return (
          <LatticeSumInputWithDecimal
            commaDashed={DecimalDashed}
            commaFilled={DecimalFilled}
            commaPostion={item.decimalPosition}
            decimalError={errors.includes(`decimalError:${item.name.split(':')[1]}`) || missingDecimal}
            decimalIndex={decimalIndex}
            inputRef={item.itemIndex === gridColumns + gridRows ? inputRef : null}
            isError={errors.includes(item.name)}
            key={`${item.name}-${index}-${inputKey}-sum`}
            name={item.name}
            onChange={handleSumValueChange}
            onCommaClick={onCommaClick}
            onEnter={onEnterPress}
            value={cellsSumAnswers[item.name]}
          />
        )
      }

      case LatticeRowType.CARR:
        return (
          <LatticeCarriedNumber
            key={`${item.name}-${index}-${inputKey}-carried`}
            name={item.name}
            onChange={handleCarriedNumberChange}
            onEnter={onEnterPress}
            value={carriedNumbers[item.name] || ''}
          />
        )

      default:
        return <div />
    }
  }

  const gridTemplateAreas = generateLatticeGridTemplate(gridColumns, gridRows)

  return (
    <ActivityWrapper className="mt-7">
      <div
        className="grid space-x-2 space-y-2"
        style={{
          gridTemplateAreas,
          justifyItems: 'center',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ gridArea: 'space' }} />
        <div style={{ gridArea: 'carriedSpace' }} />
        <div style={{ gridArea: 'space2' }}>
          <img
            className={`cursor-pointer w-3 h-3 ${
              errors.includes(`decimalError:${gridRows}`) || missingDecimal ? 'border-2 border-red rounded-4xl' : ''
            }`}
            onClick={() => onCommaClick(String(gridRows))}
            src={Number(decimalIndex) === gridRows ? DecimalFilled : DecimalDashed}
            style={{ paddingLeft: '1px', paddingTop: '1px' }}
          />
        </div>
        <div className="font-semibold text-3xl text-purple" style={{ gridArea: 'times', width: 14, height: 14 }}>
          <SVGImage alt={t('timesIcon')} srcKey="bootcamp/multi_sign.svg" />
        </div>
        {latticeArray.map((item, index) => renderItem(item, index))}
      </div>

      {actionButtonsFC &&
        actionButtonsFC({
          formValid: isFormValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: clear,
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default LatticeDecimal
